import React from 'react';

import { SkeletonBlock } from '../../../components/skeleton/skeleton';

function PlanCardSkeleton({ isFeatured = false }) {
  return (
    <div className="relative rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {isFeatured && (
        <div className="absolute left-6 top-0 -translate-y-1/2 rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm dark:bg-sky-500">
          Most popular
        </div>
      )}
      <div className={`space-y-4 ${isFeatured ? 'pt-8' : ''}`}>
        <SkeletonBlock className="h-6 w-44 rounded" />
        <div className="flex items-end gap-2">
          <SkeletonBlock className="h-12 w-20 rounded-md" />
          <SkeletonBlock className="h-5 w-24 rounded" />
        </div>
        <SkeletonBlock className="h-4 w-full max-w-md rounded" />
        <div className="space-y-3 pt-2">
          {[0, 1, 2, 3, 4].map((k) => (
            <div key={k} className="flex items-center gap-3">
              <SkeletonBlock className="h-6 w-6 rounded-md" />
              <SkeletonBlock className="h-4 w-40 rounded" />
            </div>
          ))}
        </div>
        <SkeletonBlock className="mt-4 h-10 w-full rounded-full" />
      </div>
    </div>
  );
}

/**
 * SubscriptionSkeleton renders one of two layouts:
 * - hasSubscription=true  → single-card preview (matches the "current plan"
 *   detail view shown when the company already has a subscription).
 * - hasSubscription=false → 3-card pricing comparison (matches the picker
 *   shown when the company hasn't selected a plan yet).
 *
 * Pass `hasSubscription` from the parent so the loading state doesn't flash
 * the wrong layout before data arrives.
 */
export default function SubscriptionSkeleton({ hasSubscription = false }) {
  if (hasSubscription) {
    return (
      <div className="space-y-3">
        <div className="relative flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-8">
          <div className="space-y-5">
            <SkeletonBlock className="h-6 w-44 rounded" />
            <div className="flex items-end gap-2">
              <SkeletonBlock className="h-14 w-28 rounded-md" />
              <SkeletonBlock className="h-5 w-24 rounded" />
            </div>
            <SkeletonBlock className="h-4 w-full max-w-lg rounded" />
            <div className="space-y-3 pt-2">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <SkeletonBlock className="h-6 w-6 rounded-md" />
                  <SkeletonBlock className="h-4 w-56 max-w-full rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <SkeletonBlock className="h-3 w-full max-w-3xl rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <PlanCardSkeleton isFeatured />
        <PlanCardSkeleton />
        <PlanCardSkeleton />
      </div>
      <div className="space-y-2 rounded-md border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        <SkeletonBlock className="h-3 w-full max-w-3xl rounded" />
        <SkeletonBlock className="h-3 w-full max-w-2xl rounded" />
        <SkeletonBlock className="h-3 w-full max-w-xl rounded" />
      </div>
    </div>
  );
}
